import { connect } from 'react-redux';
import i18next from 'i18next';
import moment from 'moment-timezone';
import React, { useState, useEffect } from 'react';

import { showErrorMessage, showSuccessMessage } from '@actions/messageconfirmation';

import utilsXLS from '@commons/utils/makeXLS';

import { getClientInfo } from '@selectors/client';

import { productMix as productMixService } from '@services/productMix';

import ExportModalContent from '@lib/inpulse/ExportModal';

import { createListingSheet, createAggregatedSheet, createToplineSheet } from './utils';

const DEFAULT_CATEGORY = 'none value';

const ProductMixProgressExportModal = (props) => {
  const {
    currency,
    params: { exportPeriod, selectedStoreId },
    client: { useTtc, forecastProperty, turnoverName },
    closeModal,
  } = props;

  const [productMixListByDay, setProductMixListByDay] = useState([]);
  const [nbDaysLeftToFetch, setNbDaysLeftToFetch] = useState(null);

  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [titleModal, setTitleModal] = useState(
    i18next.t('FORECAST.PRODUCT_MIX.EXPORT_IN_PROGRESS'),
  );

  useEffect(() => {
    if (!exportPeriod.startDate || !exportPeriod.endDate) {
      return;
    }

    const diffDays = moment(exportPeriod.endDate)
      .endOf('day')
      .diff(moment(exportPeriod.startDate).startOf('day'), 'days');

    setNbDaysLeftToFetch(diffDays + 1);
  }, [exportPeriod.endDate, exportPeriod.startDate]);

  useEffect(() => {
    if (!exportPeriod.startDate || !exportPeriod.endDate || nbDaysLeftToFetch == null) {
      return;
    }

    // All product mix data has been fetched
    if (nbDaysLeftToFetch <= 0) {
      setProgress(100);

      const listingDataSheet = createListingSheet(useTtc, productMixListByDay);
      const aggregatedDataSheet = createAggregatedSheet(useTtc, productMixListByDay);
      const toplineDataSheet = createToplineSheet(
        forecastProperty,
        turnoverName,
        productMixListByDay,
        currency,
      );

      utilsXLS.makeXLS(i18next.t('FORECAST.PRODUCT_MIX.EXPORT_FILENAME'), [
        utilsXLS.generateDefaultSheet(
          listingDataSheet.title,
          listingDataSheet.headers,
          listingDataSheet.data,
          currency,
        ),
        utilsXLS.generateDefaultSheet(
          aggregatedDataSheet.title,
          aggregatedDataSheet.headers,
          aggregatedDataSheet.data,
          currency,
        ),
        utilsXLS.generateDefaultSheet(
          toplineDataSheet.title,
          toplineDataSheet.headers,
          toplineDataSheet.data,
          currency,
        ),
      ]);

      return exportReady();
    }

    // Fetch product mix date by date for the selected store
    const nbDaysFetched = productMixListByDay.length;

    const date = moment(exportPeriod.startDate).add(nbDaysFetched, 'days');

    loadProductMixByDate(date);
  }, [nbDaysLeftToFetch]);

  /**
   * Get forecast product mix for given date and store
   *
   * @param {*} date
   */
  const loadProductMixByDate = async (date) => {
    const updatedProductMixList = [...productMixListByDay];

    // Perform request
    try {
      const { ca, tot, turnoverExcludingTaxes, productsByCategory } =
        await productMixService.getForecastsProductMix(selectedStoreId, date);

      const formattedProductsByCategory = productsByCategory;

      const noneCategory = productsByCategory[DEFAULT_CATEGORY];

      if (noneCategory) {
        const formattedNoneCategoryProducts = noneCategory.products.map((product) => ({
          ...product,
          category: i18next.t('GENERAL.NONE_VALUE'),
        }));

        formattedProductsByCategory[i18next.t('GENERAL.NONE_VALUE')] = {
          stats: noneCategory.stats,
          products: formattedNoneCategoryProducts,
        };

        delete formattedProductsByCategory[DEFAULT_CATEGORY];
      }

      updatedProductMixList.push({
        date,
        ca,
        tot,
        turnoverExcludingTaxes,
        productsByCategory: formattedProductsByCategory,
      });

      setProductMixListByDay(updatedProductMixList);

      updateProgress(nbDaysLeftToFetch - 1, updatedProductMixList.length, setProgress);
    } catch {
      exportFailure(setIsLoading, setTitleModal);
    }

    setNbDaysLeftToFetch(nbDaysLeftToFetch - 1);
  };

  /**  Modal functions */
  const updateProgress = (nbDaysLeftToFetch, nbDaysFetched, setProgress) => {
    const totalDaysToFetch = nbDaysLeftToFetch + nbDaysFetched;

    const updatedProgress = 100 - (nbDaysLeftToFetch / totalDaysToFetch) * 100;

    setProgress(updatedProgress);
  };

  const exitModal = () => {
    setIsLoading(false);

    closeModal();
  };

  const exportReady = () => {
    setTitleModal(i18next.t('FORECAST.PRODUCT_MIX.EXPORT_FINISHED'));
    setIsLoading(false);
  };

  const exportFailure = () => {
    setTitleModal(i18next.t('GENERAL.EXPORT_FAILURE'));
    setIsLoading(false);
  };

  return (
    <ExportModalContent
      {...props}
      closeModal={closeModal}
      exitModal={exitModal}
      isLoading={isLoading}
      progress={progress}
      setLoading={setIsLoading}
      titleModal={titleModal}
    />
  );
};

const mapStateToProps = (state) => ({
  currency: state.baseReducer.currency,
  client: getClientInfo(state.baseReducer.user),
});

const mapDispatchToProps = (dispatch) => ({
  showErrorMessage: (message) => {
    dispatch(showErrorMessage(message));
  },
  showSuccessMessage: (message) => {
    dispatch(showSuccessMessage(message));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(ProductMixProgressExportModal);
